import {
    useToast,
    Heading,
    Flex,
    Box,
    Text,
    Tooltip,
    IconButton,
    useClipboard,
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    SimpleGrid,
    Center,
    Spinner,
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { useContext, useEffect, useState } from 'react';
import { FaCopy, FaRedo } from 'react-icons/fa';
import { JSONPreview } from '../components/JSONPreview';
import config from '../config';
import SelectedGameContext from '../context/SelectedGameContext';

enum RoomState {
    'active',
    'started',
    'deleted',
}

interface ActorInfoResponse {
    type: 'success' | 'error';
    data?: {
        activePlayerCount?: number;
        isActive?: boolean;
        [k: string]: any;
    }; // payload from actor if successful
    message?: string; // if error
}

interface GameInfo {
    id: string;
    state?: RoomState;
    joinCode: string;
    owner: string;
    idpIds?: Map<string, string>;
    createdAt: string;
    updatedAt: string;
    name: string;
    sessionId: string;
    gameserverState: string;
    maxPlayers: number;
    joinable: number;
    rejoinable: number;
    localRmb?: number;
    branchName?: string;
    rmbTag?: string;
    actorTag?: string;
    actorName?: string;
    license: string;
    actorInfo?: ActorInfoResponse;
}

interface PaginatedData {
    previous: string;
    next: string;
}

interface GetRoomsResponse {
    data: GameInfo[];
    page: PaginatedData;
}

const fakeBingoData = {
    type: 'success',
    page: {},
    data: [
        {
            id: '10578edd-7328-474c-9e3c-5530232c28a5',
            state: 'active',
            joinCode: '285712',
            owner: '93e56755-8d7e-4dd5-80f0-285a2b4784d9',
            createdAt: '2023-07-26T14:38:01.190893',
            updatedAt: '2023-07-26T14:42:08.119851',
            name: '',
            sessionId: '10578edd-7328-474c-9e3c-5530232c28a5',
            gameserverState: 'active',
            maxPlayers: 2500,
            joinable: 1,
            rejoinable: 1,
            localRmb: 0,
            branchName: 'Public',
            rmbTag: '49',
            actorName: 'bingoroyale',
            license: 'd2eea033-f28d-41c6-a992-f85a55c70ab2',
            satMaxPlayer: 250,
            msTaskPool: 'task_pool_2048_cpu',
            satTaskPool: 'task_pool_256_cpu',
            actorInfo: {
                type: 'success',
                data: {
                    activePlayerCount: 2,
                    gameDuration: 159,
                    gameMode: 0,
                    hostNickname: 'billipopagh',
                    hostTwitchID: '',
                    isActive: true,
                    joinedPlayerCount: 2,
                    playersCount: 2,
                    roomState: 2,
                },
            },
        },
        {
            id: '420220c7-6dbe-405e-bfba-6b4e8f49baaf',
            state: 'active',
            joinCode: '351221',
            owner: '44abccea-250c-488a-ba75-4e5cb7bac6e8',
            createdAt: '2023-07-26T14:32:03.769858',
            updatedAt: '2023-07-26T14:42:10.468319',
            name: '',
            sessionId: '420220c7-6dbe-405e-bfba-6b4e8f49baaf',
            gameserverState: 'active',
            maxPlayers: 2500,
            joinable: 1,
            rejoinable: 1,
            localRmb: 0,
            branchName: 'Public',
            rmbTag: '49',
            actorName: 'bingoroyale',
            license: 'd2eea033-f28d-41c6-a992-f85a55c70ab2',
            satMaxPlayer: 250,
            msTaskPool: 'task_pool_2048_cpu',
            satTaskPool: 'task_pool_256_cpu',
            actorInfo: {
                type: 'success',
                data: {
                    activePlayerCount: 2,
                    gameDuration: 125,
                    gameMode: 0,
                    hostNickname: 'sahil',
                    hostTwitchID: '',
                    isActive: true,
                    joinedPlayerCount: 2,
                    playersCount: 2,
                    roomState: 2,
                },
            },
        },
        {
            id: '50eb9756-6461-4ce9-b46f-ca1492847b69',
            state: 'active',
            joinCode: '419702',
            owner: '41de481c-cc4f-4b75-9814-7fe678448d61',
            createdAt: '2023-07-26T14:05:42.164957',
            updatedAt: '2023-07-26T14:42:21.501469',
            name: '',
            sessionId: '50eb9756-6461-4ce9-b46f-ca1492847b69',
            gameserverState: 'active',
            maxPlayers: 2500,
            joinable: 1,
            rejoinable: 1,
            localRmb: 0,
            branchName: 'Public',
            rmbTag: '49',
            actorName: 'bingoroyale',
            license: 'd2eea033-f28d-41c6-a992-f85a55c70ab2',
            satMaxPlayer: 250,
            msTaskPool: 'task_pool_2048_cpu',
            satTaskPool: 'task_pool_256_cpu',
            actorInfo: {
                type: 'success',
                data: {
                    activePlayerCount: 18,
                    gameDuration: 0,
                    gameMode: null,
                    hostNickname: null,
                    hostTwitchID: null,
                    isActive: true,
                    joinedPlayerCount: 24,
                    playersCount: 18,
                    roomState: 4,
                },
            },
        },
    ],
};

export const RunningGames: React.FC<{}> = () => {
    const selectedGame = useContext(SelectedGameContext);
    const currLicense = selectedGame?.currLicense;
    const { keycloak } = useKeycloak();
    const toast = useToast();
    const {
        value: clipboardValue,
        setValue: setClipboardValue,
        hasCopied,
        onCopy,
    } = useClipboard('');
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [itemsLoaded, setItemsLoaded] = useState(false);
    const [sessionOwners, setSessionOwners] = useState<Map<string, string>>(new Map());

    const [sessions, setSessions] = useState<GameInfo[]>([]);

    const loadSessionsPaginated = async (page?: PaginatedData): Promise<GameInfo[]> => {
        let url = `${config.lobbyServiceURL}/rooms?license=${currLicense.key}`;
        if (page) {
            url = `${config.lobbyServiceURL}/rooms?${page.next}`;
        }

        const resp = await fetch(url, {
            headers: {
                Authorization: `Bearer ${keycloak.token!}`,
            },
        });
        if (resp.status !== 200) {
            toast({
                title: 'Unable to get information',
                description: 'Unable to get user information',
                status: 'error',
                duration: 9000,
                variant: 'subtle',
                isClosable: true,
            });
            return [];
        }

        // TODO: remove fake data
        let respData = await resp.json();
        // respData = fakeBingoData;
        if (!respData.data || respData.data.length === 0) {
            return [];
        }

        let moreSessions: GameInfo[] = [];
        if (respData.page && respData.page.next) {
            moreSessions = await loadSessionsPaginated(respData.page);
        }
        return [...respData.data, ...moreSessions];
    };

    const loadSessions = async () => {
        const sessions = await loadSessionsPaginated();
        await loadSessionOwners(sessions.map((session) => session.owner));
        setSessions(sessions);
        setItemsLoaded(true);
    };

    const loadSessionOwners = async (ownerIDs: string[]) => {
        const ownerResps = await Promise.allSettled(
            ownerIDs.map((ownerID: string) =>
                fetch(`${config.socialServiceURL}/user/${ownerID}/profile/public`),
            ),
        );
        const ownerNames = await Promise.all(
            ownerResps.map(async (ownerResp) => {
                if (ownerResp.status !== 'fulfilled') {
                    return null;
                }
                const fetchResp: Response = ownerResp.value;
                if (fetchResp.status !== 200) {
                    return null;
                }
                const fetchRespBody = await fetchResp.json();
                return fetchRespBody.data.username;
            }),
        );
        const sessionsMap = new Map<string, string>();
        ownerNames.forEach((ownerName, i) => {
            sessionsMap.set(ownerIDs[i], ownerName);
        });
        setSessionOwners(sessionsMap);
    };

    useEffect(() => {
        if (!currLicense) {
            return;
        }
        setItemsLoaded(false);
        loadSessions();
    }, [refreshCounter, currLicense]);

    useEffect(() => {
        setClipboardValue('');
    }, []);

    useEffect(() => {
        console.log('sessions', sessions);
    }, [sessions]);

    useEffect(() => {
        if (hasCopied) {
            toast({
                description: 'Session ID copied to clipboard',
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
            setClipboardValue('');
        }
    }, [hasCopied]);

    useEffect(() => {
        if (clipboardValue) {
            onCopy();
        }
    }, [clipboardValue]);

    return (
        <>
            <Heading>Running Games</Heading>
            <IconButton
                icon={<FaRedo />}
                onClick={() => setRefreshCounter((count) => count + 1)}
                aria-label={''}
            />
            <Flex direction={'column'}>
                {itemsLoaded ? (
                    sessions.length > 0 ? (
                        sessions.map((session, i) => (
                            <Box
                                key={`session_${i}`}
                                border="1px solid black"
                                borderRadius="0.25em"
                            >
                                <SimpleGrid columns={3} spacing={2}>
                                    <Center>
                                        <Text>Session ID:</Text>
                                    </Center>
                                    <Center>
                                        <Text>{session.id}</Text>
                                    </Center>
                                    <Center>
                                        <Tooltip label="Copy to Clipboard">
                                            <IconButton
                                                size="md"
                                                variant="ghost"
                                                onClick={() => {
                                                    setClipboardValue(session.id);
                                                }}
                                                marginLeft={'1em'}
                                                aria-label="Copy Session ID to Clipboard"
                                                icon={<FaCopy />}
                                            />
                                        </Tooltip>
                                    </Center>
                                </SimpleGrid>
                                <SimpleGrid columns={3} spacing={2}>
                                    <Center>
                                        <Text>Session Owner:</Text>
                                    </Center>
                                    <Center>
                                        <Text>{sessionOwners.get(session.owner)}</Text>
                                    </Center>
                                </SimpleGrid>
                                <Accordion allowToggle>
                                    <AccordionItem>
                                        <AccordionButton>
                                            <AccordionIcon />
                                            Session Info JSON
                                        </AccordionButton>
                                        <AccordionPanel>
                                            <JSONPreview caption={''} data={session}></JSONPreview>
                                        </AccordionPanel>
                                    </AccordionItem>
                                </Accordion>
                            </Box>
                        ))
                    ) : (
                        <Box>There are no currently running games</Box>
                    )
                ) : (
                    <Center width="100%" height="100%">
                        <Spinner data-cy="loading" />
                    </Center>
                )}
            </Flex>
        </>
    );
};
